import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Users, Check, GraduationCap, Briefcase, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { PROGRAM_DETAILS, PROGRAM_SLUGS, COLLEGE_JSONLD, SITE_URL } from "../data/siteContent";

export default function ProgramDetail() {
  const { slug } = useParams();
  const detail = PROGRAM_DETAILS[slug];
  const [course, setCourse] = useState(null);
  const [allCourses, setAllCourses] = useState([]);

  useEffect(() => {
    api.get("/courses")
      .then((r) => {
        setAllCourses(r.data);
        setCourse(r.data.find((c) => detail && c.short_name === detail.shortName) || null);
      })
      .catch(() => {});
  }, [detail]);

  const jsonLd = useMemo(() => {
    if (!detail) return [];
    const title = course?.title || detail.shortName;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Course",
        name: `${title} at SVS College of Nursing, Bokaro`,
        description: detail.metaDescription,
        provider: COLLEGE_JSONLD,
        url: SITE_URL + detail.path,
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: detail.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Programs", item: SITE_URL + "/courses" },
          { "@type": "ListItem", position: 3, name: title, item: SITE_URL + detail.path },
        ],
      },
    ];
  }, [detail, course]);

  if (!detail) {
    return (
      <div data-testid="program-not-found">
        <PageHero
          overline="Programs"
          title={<>Program not <span className="italic text-gold-gradient">found</span></>}
          subtitle="The program you are looking for does not exist. Explore our nursing programs below."
        />
        <section className="bg-sand py-24 text-center">
          <Link
            to="/courses"
            data-testid="program-not-found-back"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-sand font-semibold px-7 py-3.5 transition-transform duration-300 hover:scale-105"
          >
            View all programs <ArrowUpRight size={18} />
          </Link>
        </section>
      </div>
    );
  }

  const title = course?.title || detail.shortName;
  const others = allCourses.filter((c) => c.short_name !== detail.shortName);

  return (
    <div data-testid={`program-page-${slug}`}>
      <SEO title={detail.metaTitle} description={detail.metaDescription} path={detail.path} jsonLd={jsonLd} />

      <PageHero
        overline={`Program · ${course?.duration || ""}`}
        title={<>{title} <span className="italic text-gold-gradient">at SVS</span></>}
        subtitle={detail.tagline}
      />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-sand border-b border-navy/10" data-testid="program-breadcrumb">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-2 text-sm text-navy/60">
          <Link to="/" className="hover:text-gold transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/courses" className="hover:text-gold transition-colors">Programs</Link>
          <ChevronRight size={14} />
          <span className="text-navy font-medium">{title}</span>
        </div>
      </nav>

      {/* Overview + quick facts */}
      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Overview</span>
              <h2 className="font-serif text-4xl text-navy mt-4 mb-8">About this program</h2>
            </Reveal>
            {detail.overview.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="text-navy/70 leading-relaxed mb-6">{p}</p>
              </Reveal>
            ))}
            {(course?.highlights || []).length > 0 && (
              <Reveal delay={0.15}>
                <ul className="mt-8 space-y-3">
                  {course.highlights.map((h, i) => (
                    <li key={i} className="flex gap-3 text-navy/80">
                      <Check size={20} className="text-gold shrink-0" /> {h}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="bg-navy text-sand rounded-[2px] p-8 lg:p-10 relative overflow-hidden" data-testid="program-facts">
                <div className="grain-overlay" />
                <div className="relative space-y-6">
                  <h3 className="font-serif text-2xl">Quick facts</h3>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                      <Clock size={18} className="text-gold" />
                      <span className="text-sand/60">Duration</span>
                      <span className="ml-auto font-semibold">{course?.duration}</span>
                    </div>
                    <div className="flex items-center gap-3 border-b border-white/10 pb-4">
                      <Users size={18} className="text-gold" />
                      <span className="text-sand/60">Intake</span>
                      <span className="ml-auto font-semibold">{course?.seats}</span>
                    </div>
                    <div className="flex items-start gap-3 border-b border-white/10 pb-4">
                      <GraduationCap size={18} className="text-gold shrink-0 mt-0.5" />
                      <span className="text-sand/60">Eligibility</span>
                      <span className="ml-auto font-semibold text-right">{course?.eligibility}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase size={18} className="text-gold" />
                      <span className="text-sand/60">Placement</span>
                      <span className="ml-auto font-semibold text-gold-gradient font-serif text-lg">100% Guaranteed</span>
                    </div>
                  </div>
                  <Link
                    to="/admissions"
                    data-testid="program-apply-btn"
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy font-bold px-6 py-4 transition-transform duration-300 hover:scale-[1.03]"
                  >
                    Apply for 2026-27 <ArrowUpRight size={18} />
                  </Link>
                  <Link
                    to="/admissions/eligibility"
                    data-testid="program-eligibility-link"
                    className="block text-center text-sm text-sand/70 hover:text-gold transition-colors"
                  >
                    View full eligibility criteria
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What you'll study */}
      <section className="bg-white py-24" data-testid="program-study">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Curriculum</span>
            <h2 className="font-serif text-4xl text-navy mt-4 mb-12">What you will study</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {detail.study.map((s, i) => (
              <Reveal key={s} delay={i * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.3 }}
                  className="border border-navy/10 rounded-[2px] p-6 h-full flex items-center gap-4 bg-sand"
                >
                  <span className="font-serif text-2xl text-gold/50">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-navy font-medium">{s}</span>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="bg-sand py-24" data-testid="program-careers">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Careers</span>
              <h2 className="font-serif text-4xl text-navy mt-4 leading-tight">
                Where this degree <span className="italic text-gold-gradient">takes you</span>
              </h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                Graduates of {title} step into respected roles across hospitals, public health and
                education — in India and abroad.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <div className="space-y-4">
              {detail.careers.map((c, i) => (
                <Reveal key={c} delay={i * 0.06}>
                  <div className="flex gap-5 items-center bg-white border border-navy/10 rounded-[2px] p-5">
                    <span className="font-serif text-3xl text-gold/40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-navy/80">{c}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24" data-testid="program-faq">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Questions</span>
              <h2 className="font-serif text-4xl text-navy mt-4">Frequently asked questions</h2>
            </div>
          </Reveal>
          <div className="space-y-5">
            {detail.faqs.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border border-navy/10 rounded-[2px] p-6 bg-sand" data-testid={`program-faq-${i}`}>
                  <h3 className="font-serif text-xl text-navy mb-2">{f.q}</h3>
                  <p className="text-navy/60 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Other programs */}
      {others.length > 0 && (
        <section className="bg-sand py-20" data-testid="program-others">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <h2 className="font-serif text-3xl text-navy mb-8">Explore other programs</h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-5">
              {others.map((c, i) => (
                <Reveal key={c.id} delay={i * 0.08}>
                  <Link
                    to={`/programs/${PROGRAM_SLUGS[c.short_name] || ""}`}
                    data-testid={`other-program-${c.short_name}`}
                    className="group flex items-center justify-between bg-white border border-navy/10 rounded-[2px] p-7 transition-colors duration-300 hover:border-gold"
                  >
                    <div>
                      <div className="font-serif text-xl text-navy">{c.title}</div>
                      <div className="text-sm text-navy/50 mt-1">{c.duration} · {c.seats}</div>
                    </div>
                    <ArrowUpRight size={22} className="text-gold transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-navy py-20 text-center relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-6">
            Start your {title} journey
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/admissions"
              data-testid="program-cta-apply"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Apply for 2026-27 <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/admissions/fees"
              data-testid="program-cta-fees"
              className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Fees &amp; financial guidance
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
